import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Command } from 'cmdk';
import { Dialog } from 'radix-ui';
import {
  Search,
  LayoutDashboard,
  Package,
  ChefHat,
  Truck,
  UtensilsCrossed,
  Palette,
  ShoppingCart,
  Calculator,
  FileText,
  Settings,
  Clock,
  BarChart3,
  Plus,
  ScanLine,
  Camera,
  Sparkles,
  FileSpreadsheet,
} from 'lucide-react';
import { ingredientsApi, recipesApi, suppliersApi } from '@/lib/api';
import { useCurrentRestaurantId } from '@/stores/authStore';
import type { Ingredient, Recipe, Supplier } from '@/lib/api';

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const navigate = useNavigate();
  const restaurantId = useCurrentRestaurantId();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Global keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Debounced search
  const search = useCallback(
    (q: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);

      if (!q.trim() || !restaurantId) {
        setIngredients([]);
        setRecipes([]);
        setSuppliers([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      debounceRef.current = setTimeout(async () => {
        try {
          const [ingRes, recRes, supRes] = await Promise.all([
            ingredientsApi.list({ restaurant_id: restaurantId, search: q, per_page: 5 }),
            recipesApi.list({ restaurant_id: restaurantId, search: q, per_page: 5 }),
            suppliersApi.list({ restaurant_id: restaurantId, search: q, per_page: 5 }),
          ]);
          setIngredients(ingRes.data.data || []);
          setRecipes(recRes.data.data || []);
          setSuppliers(supRes.data.data || []);
        } catch {
          // Silently fail — user keeps typing
        } finally {
          setIsSearching(false);
        }
      }, 300);
    },
    [restaurantId]
  );

  useEffect(() => {
    search(query);
  }, [query, search]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setQuery('');
      setIngredients([]);
      setRecipes([]);
      setSuppliers([]);
    }
  }, [open]);

  const select = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  const hasResults = ingredients.length > 0 || recipes.length > 0 || suppliers.length > 0;

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
        <Dialog.Content className="fixed inset-x-3 sm:inset-x-auto sm:left-1/2 top-[10%] sm:top-[20%] z-50 sm:w-full sm:max-w-xl sm:-translate-x-1/2 rounded-2xl bg-white shadow-2xl ring-1 ring-black/5 focus:outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 overflow-hidden">
          <Dialog.Title className="sr-only">Buscar</Dialog.Title>
          <Command className="flex flex-col" shouldFilter={false}>
            {/* Search input */}
            <div className="flex items-center gap-3 px-4 border-b border-slate-200">
              <Search className="w-5 h-5 text-slate-400 flex-shrink-0" />
              <Command.Input
                value={query}
                onValueChange={setQuery}
                placeholder="Buscar ingredientes, recetas, acciones..."
                className="flex-1 h-14 bg-transparent text-base text-slate-900 placeholder:text-slate-400 focus:outline-none"
              />
              <kbd className="hidden sm:inline-flex items-center gap-0.5 px-2 py-1 text-xs text-slate-400 bg-slate-100 rounded border border-slate-200">
                ESC
              </kbd>
            </div>

            {/* Results */}
            <Command.List className="max-h-[60vh] overflow-y-auto p-2">
              {isSearching && (
                <Command.Loading>
                  <div className="py-4 text-center text-sm text-slate-500">Buscando...</div>
                </Command.Loading>
              )}

              {query.trim() && !isSearching && !hasResults && (
                <Command.Empty className="py-8 text-center text-sm text-slate-500">
                  No se encontraron resultados para "{query}"
                </Command.Empty>
              )}

              {/* Search results */}
              {ingredients.length > 0 && (
                <Command.Group heading="Ingredientes" className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-slate-400 [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider">
                  {ingredients.map((ing) => (
                    <Command.Item
                      key={`ing-${ing.id}`}
                      value={`ingredient-${ing.name}`}
                      onSelect={() => select(`/ingredients/${ing.id}`)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm text-slate-700 data-[selected=true]:bg-blue-50 data-[selected=true]:text-blue-900"
                    >
                      <Package className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span className="flex-1 truncate">{ing.name}</span>
                      {ing.category && (
                        <span className="text-xs text-slate-400">{ing.category.icon} {ing.category.name}</span>
                      )}
                    </Command.Item>
                  ))}
                </Command.Group>
              )}

              {recipes.length > 0 && (
                <Command.Group heading="Recetas" className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-slate-400 [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider">
                  {recipes.map((rec) => (
                    <Command.Item
                      key={`rec-${rec.id}`}
                      value={`recipe-${rec.name}`}
                      onSelect={() => select(`/recipes/${rec.id}`)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm text-slate-700 data-[selected=true]:bg-blue-50 data-[selected=true]:text-blue-900"
                    >
                      <ChefHat className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span className="flex-1 truncate">{rec.name}</span>
                      {rec.pvp && (
                        <span className="text-xs text-slate-400">{rec.pvp}€</span>
                      )}
                    </Command.Item>
                  ))}
                </Command.Group>
              )}

              {suppliers.length > 0 && (
                <Command.Group heading="Proveedores" className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-slate-400 [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider">
                  {suppliers.map((sup) => (
                    <Command.Item
                      key={`sup-${sup.id}`}
                      value={`supplier-${sup.name}`}
                      onSelect={() => select(`/suppliers/${sup.id}`)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm text-slate-700 data-[selected=true]:bg-blue-50 data-[selected=true]:text-blue-900"
                    >
                      <Truck className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span className="flex-1 truncate">{sup.name}</span>
                      {sup.city && (
                        <span className="text-xs text-slate-400">{sup.city}</span>
                      )}
                    </Command.Item>
                  ))}
                </Command.Group>
              )}

              {/* Quick actions — always visible */}
              {!query.trim() && (
                <>
                  <Command.Group heading="Acciones rápidas" className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-slate-400 [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider">
                    <Command.Item value="nuevo-ingrediente" onSelect={() => select('/ingredients/new')} className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm text-slate-700 data-[selected=true]:bg-blue-50 data-[selected=true]:text-blue-900">
                      <Plus className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>Nuevo ingrediente</span>
                    </Command.Item>
                    <Command.Item value="nueva-receta" onSelect={() => select('/recipes/new')} className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm text-slate-700 data-[selected=true]:bg-blue-50 data-[selected=true]:text-blue-900">
                      <Plus className="w-4 h-4 text-purple-500 flex-shrink-0" />
                      <span>Nueva receta</span>
                    </Command.Item>
                    <Command.Item value="nuevo-proveedor" onSelect={() => select('/suppliers/new')} className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm text-slate-700 data-[selected=true]:bg-blue-50 data-[selected=true]:text-blue-900">
                      <Plus className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <span>Nuevo proveedor</span>
                    </Command.Item>
                  </Command.Group>

                  <Command.Group heading="Escanear e Importar" className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-slate-400 [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider">
                    <Command.Item value="escanear-albaran ocr factura" onSelect={() => select('/importar?action=ocr')} className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm text-slate-700 data-[selected=true]:bg-blue-50 data-[selected=true]:text-blue-900">
                      <ScanLine className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <span>Escanear Albarán</span>
                    </Command.Item>
                    <Command.Item value="importar-carta-ia menu scan" onSelect={() => select('/importar?action=menu-scan')} className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm text-slate-700 data-[selected=true]:bg-blue-50 data-[selected=true]:text-blue-900">
                      <Camera className="w-4 h-4 text-purple-500 flex-shrink-0" />
                      <span>Importar Carta con IA</span>
                    </Command.Item>
                    <Command.Item value="generar-receta-ia crear" onSelect={() => select('/importar?action=ai-recipe')} className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm text-slate-700 data-[selected=true]:bg-blue-50 data-[selected=true]:text-blue-900">
                      <Sparkles className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      <span>Generar Receta con IA</span>
                    </Command.Item>
                    <Command.Item value="importar-excel ingredientes csv" onSelect={() => select('/importar?action=excel')} className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm text-slate-700 data-[selected=true]:bg-blue-50 data-[selected=true]:text-blue-900">
                      <FileSpreadsheet className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>Importar Excel</span>
                    </Command.Item>
                  </Command.Group>

                  <Command.Group heading="Navegación" className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-slate-400 [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider">
                    <Command.Item value="dashboard" onSelect={() => select('/dashboard')} className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm text-slate-700 data-[selected=true]:bg-blue-50 data-[selected=true]:text-blue-900">
                      <LayoutDashboard className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span>Dashboard</span>
                    </Command.Item>
                    <Command.Item value="ingredientes" onSelect={() => select('/ingredients')} className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm text-slate-700 data-[selected=true]:bg-blue-50 data-[selected=true]:text-blue-900">
                      <Package className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span>Ingredientes</span>
                    </Command.Item>
                    <Command.Item value="recetas" onSelect={() => select('/recipes')} className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm text-slate-700 data-[selected=true]:bg-blue-50 data-[selected=true]:text-blue-900">
                      <ChefHat className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span>Recetas</span>
                    </Command.Item>
                    <Command.Item value="menus" onSelect={() => select('/menus')} className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm text-slate-700 data-[selected=true]:bg-blue-50 data-[selected=true]:text-blue-900">
                      <UtensilsCrossed className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span>Menús</span>
                    </Command.Item>
                    <Command.Item value="cartas" onSelect={() => select('/cartas')} className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm text-slate-700 data-[selected=true]:bg-blue-50 data-[selected=true]:text-blue-900">
                      <Palette className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span>Cartas</span>
                    </Command.Item>
                    <Command.Item value="pedidos" onSelect={() => select('/orders')} className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm text-slate-700 data-[selected=true]:bg-blue-50 data-[selected=true]:text-blue-900">
                      <ShoppingCart className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span>Pedidos</span>
                    </Command.Item>
                    <Command.Item value="planificador" onSelect={() => select('/purchase-planner')} className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm text-slate-700 data-[selected=true]:bg-blue-50 data-[selected=true]:text-blue-900">
                      <Calculator className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span>Planificador</span>
                    </Command.Item>
                    <Command.Item value="proveedores" onSelect={() => select('/suppliers')} className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm text-slate-700 data-[selected=true]:bg-blue-50 data-[selected=true]:text-blue-900">
                      <Truck className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span>Proveedores</span>
                    </Command.Item>
                    <Command.Item value="albaranes" onSelect={() => select('/albaranes')} className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm text-slate-700 data-[selected=true]:bg-blue-50 data-[selected=true]:text-blue-900">
                      <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span>Albaranes</span>
                    </Command.Item>
                    <Command.Item value="ventas-pos" onSelect={() => select('/sales')} className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm text-slate-700 data-[selected=true]:bg-blue-50 data-[selected=true]:text-blue-900">
                      <BarChart3 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span>Ventas & POS</span>
                    </Command.Item>
                    <Command.Item value="control-horario" onSelect={() => select('/time-clock')} className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm text-slate-700 data-[selected=true]:bg-blue-50 data-[selected=true]:text-blue-900">
                      <Clock className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span>Control Horario</span>
                    </Command.Item>
                    <Command.Item value="reportes" onSelect={() => select('/reports')} className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm text-slate-700 data-[selected=true]:bg-blue-50 data-[selected=true]:text-blue-900">
                      <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span>Reportes</span>
                    </Command.Item>
                    <Command.Item value="configuracion" onSelect={() => select('/settings')} className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer text-sm text-slate-700 data-[selected=true]:bg-blue-50 data-[selected=true]:text-blue-900">
                      <Settings className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span>Configuración</span>
                    </Command.Item>
                  </Command.Group>
                </>
              )}
            </Command.List>

            {/* Footer — keyboard hints hidden on mobile (touch users don't need them) */}
            <div className="hidden sm:flex items-center gap-4 px-4 py-2.5 border-t border-slate-200 text-xs text-slate-400">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-slate-100 rounded border border-slate-200">↑↓</kbd>
                navegar
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-slate-100 rounded border border-slate-200">↵</kbd>
                seleccionar
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-slate-100 rounded border border-slate-200">esc</kbd>
                cerrar
              </span>
            </div>
          </Command>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
